import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, TrendingUp, Gift, Copy, Check, Link2, MousePointerClick } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";

type PartnerReferral = {
  id: string;
  email: string;
  createdAt: string;
  partnerSource: string;
  subscriptionStatus: string;
  subscriptionTier: string | null;
  partnerCode: string | null;
  partnerCodeGeneratedAt: string | null;
  partnerRewardClaimed: boolean;
  partnerRewardClaimedAt: string | null;
};

type Stats = {
  totalSignups: number;
  conversions: number;
  conversionPct: number;
  claimed: number;
  claimedPct: number;
};

type AdminReferralsResponse = {
  stats: Stats;
  referrals: PartnerReferral[];
};

const TIER_LABELS: Record<string, string> = {
  lite: "Lite",
  starter: "Starter",
  pro: "Pro",
  trial: "Trial",
};

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function SubBadge({ status, tier }: { status: string; tier: string | null }) {
  if (status === "active" && tier) {
    return <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{TIER_LABELS[tier] ?? tier}</Badge>;
  }
  if (status === "trial") return <Badge variant="outline" className="text-xs">Trial</Badge>;
  if (status === "cancelled") return <Badge variant="destructive" className="text-xs">Cancelled</Badge>;
  return <Badge variant="secondary" className="text-xs">Inactive</Badge>;
}

type SlugPartner = {
  userId: string;
  email: string | null;
  companyName: string | null;
  slug: string | null;
  slugCreatedAt: string | null;
  totalClicks: number;
  totalSignups: number;
  totalPaidConversions: number;
};

function SlugPartnersSection() {
  const [copied, setCopied] = useState<string | null>(null);
  const { data, isLoading } = useQuery<{ partners: SlugPartner[] }>({
    queryKey: ["/api/admin/slug-partners"],
  });

  const copySlug = (slug: string) => {
    navigator.clipboard.writeText(`https://thepropertool.com/${slug}`);
    setCopied(slug);
    setTimeout(() => setCopied(null), 2000);
  };

  const partners = data?.partners ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Link2 className="h-4 w-4" />
          Referral Link Slugs
        </CardTitle>
        <CardDescription>Users who have generated a custom referral link</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-4">Loading...</p>
        ) : partners.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No users have generated a referral link yet</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{partners.length}</p>
                <p className="text-xs text-muted-foreground">Active slugs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{partners.reduce((s, p) => s + p.totalClicks, 0)}</p>
                <p className="text-xs text-muted-foreground">Total clicks</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{partners.reduce((s, p) => s + p.totalSignups, 0)}</p>
                <p className="text-xs text-muted-foreground">Signups</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Slug</TableHead>
                    <TableHead className="text-xs">Company</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                    <TableHead className="text-xs text-right">Clicks</TableHead>
                    <TableHead className="text-xs text-right">Signups</TableHead>
                    <TableHead className="text-xs text-right">Paid</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((p) => (
                    <TableRow key={p.userId}>
                      <TableCell className="text-xs">{p.email ?? "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">/{p.slug}</code>
                          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copySlug(p.slug!)}>
                            {copied === p.slug ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.companyName ?? "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(p.slugCreatedAt)}</TableCell>
                      <TableCell className="text-xs text-right font-semibold">{p.totalClicks}</TableCell>
                      <TableCell className="text-xs text-right font-semibold">{p.totalSignups}</TableCell>
                      <TableCell className="text-xs text-right font-semibold">{p.totalPaidConversions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminPartnerReferralsPage() {
  const { toast } = useToast();
  const [sourceFilter, setSourceFilter] = useState("all");
  const [subFilter, setSubFilter] = useState("all");
  const [claimedFilter, setClaimedFilter] = useState("all");
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<AdminReferralsResponse>({
    queryKey: ["/api/admin/partner-referrals"],
  });

  const markClaimedMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiRequest("PATCH", `/api/admin/partner-referrals/${userId}/mark-claimed`, {});
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partner-referrals"] });
      toast({ title: "Marked as claimed" });
    },
  });

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const stats = data?.stats;
  const referrals = (data?.referrals ?? []).filter((r) => {
    if (sourceFilter !== "all" && r.partnerSource !== sourceFilter) return false;
    if (subFilter === "active" && r.subscriptionStatus !== "active") return false;
    if (subFilter === "trial" && r.subscriptionStatus !== "trial") return false;
    if (subFilter === "inactive" && r.subscriptionStatus === "active") return false;
    if (claimedFilter === "yes" && !r.partnerRewardClaimed) return false;
    if (claimedFilter === "no" && r.partnerRewardClaimed) return false;
    return true;
  });

  return (
    <>
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-lg sm:text-2xl font-bold">Partner Referrals</h1>
        <p className="text-muted-foreground text-sm">Track StructSpectrum and other partner channel performance</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Total Signups</p>
              <p className="text-2xl font-bold">{stats?.totalSignups ?? "—"}</p>
              <p className="text-xs text-muted-foreground">from partner channels</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <TrendingUp className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Paid Conversions</p>
              <p className="text-2xl font-bold">{stats?.conversions ?? "—"}</p>
              <p className="text-xs text-muted-foreground">{stats ? `${stats.conversionPct}% conversion rate` : ""}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Gift className="h-8 w-8 text-primary shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Rewards Claimed</p>
              <p className="text-2xl font-bold">{stats?.claimed ?? "—"}</p>
              <p className="text-xs text-muted-foreground">{stats ? `${stats.claimedPct}% of eligible` : ""}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Referral Records</CardTitle>
          <CardDescription>Filter and review all partner referral signups</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-44 h-8 text-xs">
                <SelectValue placeholder="Partner source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sources</SelectItem>
                <SelectItem value="structspectrum">StructSpectrum</SelectItem>
              </SelectContent>
            </Select>
            <Select value={subFilter} onValueChange={setSubFilter}>
              <SelectTrigger className="w-44 h-8 text-xs">
                <SelectValue placeholder="Subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="active">Paid active</SelectItem>
                <SelectItem value="trial">On trial</SelectItem>
                <SelectItem value="inactive">Inactive/cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={claimedFilter} onValueChange={setClaimedFilter}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Reward status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rewards</SelectItem>
                <SelectItem value="yes">Claimed</SelectItem>
                <SelectItem value="no">Not claimed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Loading...</p>
          ) : referrals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No referrals match the current filters</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Email</TableHead>
                    <TableHead className="text-xs">Signup</TableHead>
                    <TableHead className="text-xs">Source</TableHead>
                    <TableHead className="text-xs">Plan</TableHead>
                    <TableHead className="text-xs">Code</TableHead>
                    <TableHead className="text-xs">Code Date</TableHead>
                    <TableHead className="text-xs">Claimed</TableHead>
                    <TableHead className="text-xs">Claimed Date</TableHead>
                    <TableHead className="text-xs w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="text-xs font-medium">{r.email}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(r.createdAt)}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="text-xs capitalize">{r.partnerSource}</Badge>
                      </TableCell>
                      <TableCell><SubBadge status={r.subscriptionStatus} tier={r.subscriptionTier} /></TableCell>
                      <TableCell>
                        {r.partnerCode ? (
                          <div className="flex items-center gap-1">
                            <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{r.partnerCode}</code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyCode(r.partnerCode!, r.id)}
                            >
                              {copiedId === r.id ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(r.partnerCodeGeneratedAt)}</TableCell>
                      <TableCell>
                        {r.partnerRewardClaimed
                          ? <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Claimed</Badge>
                          : <Badge variant="outline" className="text-xs">Pending</Badge>
                        }
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(r.partnerRewardClaimedAt)}</TableCell>
                      <TableCell>
                        {r.partnerCode && !r.partnerRewardClaimed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-7"
                            onClick={() => markClaimedMutation.mutate(r.id)}
                            disabled={markClaimedMutation.isPending}
                          >
                            Mark claimed
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    <div className="px-4 sm:px-6 pb-6 max-w-6xl mx-auto">
      <SlugPartnersSection />
    </div>
    </>
  );
}
